import {
  Box,
  Button,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";

import { TABLE_CONTAINER } from "../../theme/tablestyles";

const METRIC_COLUMNS = [
  { key: "active_members", label: "Active Members" },
  { key: "new_members", label: "New Members" },
  { key: "member_revenue", label: "Total Revenue from Members", format: "currency" },
  { key: "inquiries", label: "Inquiries" },
  { key: "leads", label: "Leads - In Conversation With" },
  { key: "qualified_opportunities", label: "Qualified Opportunities" },
  { key: "rfis_responded_to", label: "RFIs Responded To" },
  { key: "prospect_closed_won", label: "Prospects (Closed Won)" },
  { key: "prospect_closed_lost", label: "Prospects (Closed Lost)" },
  { key: "prospects_added", label: "Prospects Added" },
  { key: "website_visitors", label: "Website Visitors" },
  { key: "bre_companies", label: "BR&E Companies Tracked" },
  { key: "jobs", label: "Jobs Created" },
  { key: "investment", label: "Investment", format: "currency" },
  { key: "projects_won", label: "New Projects" },
];

const HEADER_CELL_SX = {
  color: "#00336C",
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  py: 1.5,
  px: 2,
  borderBottom: "1px solid #e2e8f0",
  backgroundColor: "#f8fafc",
  whiteSpace: "nowrap",
};

const ROW_CELL_SX = {
  fontSize: 12.5,
  fontVariantNumeric: "tabular-nums",
  py: 1.5,
  px: 2,
  borderBottom: "1px solid #f1f5f9",
  whiteSpace: "nowrap",
};

export default function MetricsTable({
  rows = [],
  totals = {},
  selectedMetrics = [],
  dashboardLayout = false,
  onExportCsv,
}) {
  const dashboardColumns = [
    "inquiries",
    "leads",
    "qualified_opportunities",
    "rfis_responded_to",
    "prospect_closed_won",
    "prospect_closed_lost",
    "prospects_added",
    "active_members",
    "new_members",
    "member_revenue",
    "website_visitors",
    "bre_companies",
    "jobs",
  ];
  const visibleColumns = METRIC_COLUMNS.filter(
    (m) =>
      selectedMetrics.includes(m.key) &&
      (!dashboardLayout || dashboardColumns.includes(m.key)),
  );

  const formatPeriod = (period) => {
    if (!dashboardLayout || !period) return period;
    const [year, month] = String(period).split("-");
    if (!year || !month) return period;
    return new Date(Number(year), Number(month) - 1).toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  };

  const formatValue = (val, format) => {
    if (val == null) return "—";
    if (format === "currency") return `$${val.toLocaleString()}`;
    return val.toLocaleString();
  };

  if (dashboardLayout) {
    return (
      <Box
        sx={{
          border: "1px solid #e2e8f0",
          borderRadius: 3,
          backgroundColor: "#fff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 6px 20px rgba(0,51,108,0.07)",
          overflow: "hidden",
        }}
      >
        {/* Header bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 3,
            py: 1.75,
            borderBottom: "1px solid #f1f5f9",
            background: "linear-gradient(to right, #f8fdf4, #fff)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "#648315",
                boxShadow: "0 0 0 2px #e7f0da",
              }}
            />
            <Typography
              sx={{ color: "#00336C", fontSize: 13.5, fontWeight: 800, letterSpacing: "0.01em" }}
            >
              Monthly Breakdown
            </Typography>
          </Box>

          <Button
            onClick={onExportCsv}
            startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
            sx={{
              ml: "auto",
              height: 30,
              px: 1.75,
              borderRadius: 99,
              border: "1px solid #e2e8f0",
              color: "#00336C",
              textTransform: "none",
              fontSize: 11.5,
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#f8fafc",
                borderColor: "#cbd5e1",
                color: "#00336C",
              },
            }}
          >
            Export CSV
          </Button>
        </Box>

        <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: { xs: 760, md: 1000 } }}>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ ...HEADER_CELL_SX, px: 2.5 }}>Period</TableCell>
                {visibleColumns.map((m) => (
                  <TableCell key={m.key} align="center" sx={HEADER_CELL_SX}>
                    {m.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((r) => (
                <TableRow
                  key={r.period}
                  sx={{
                    "&:hover": { backgroundColor: "#f0f7ff" },
                    transition: "background-color 0.12s ease",
                  }}
                >
                  <TableCell
                    align="center"
                    sx={{
                      ...ROW_CELL_SX,
                      px: 2.5,
                      color: "#00336C",
                      fontWeight: 700,
                    }}
                  >
                    {formatPeriod(r.period)}
                  </TableCell>
                  {visibleColumns.map((m) => {
                    const val = r[m.key];
                    return (
                      <TableCell
                        key={m.key}
                        align="center"
                        sx={{
                          ...ROW_CELL_SX,
                          color: "#00336C",
                          fontWeight: 600,
                        }}
                      >
                        {formatValue(val, m.format)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <TableContainer
      sx={{
        ...TABLE_CONTAINER,
        border: "1px solid #e8eef5",
        borderRadius: 3,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.04)",
        width: "100%",
        overflowX: "auto",
      }}
    >
      <Table size="small" sx={{ minWidth: { xs: 760, md: 920 } }}>
        <TableHead>
          <TableRow>
            <TableCell
              align="center"
              sx={{ ...HEADER_CELL_SX, px: 2.5, backgroundColor: "#eef3fb" }}
            >
              Period
            </TableCell>
            {visibleColumns.map((m) => (
              <TableCell
                key={m.key}
                align="center"
                sx={{ ...HEADER_CELL_SX, backgroundColor: "#eef3fb" }}
              >
                {m.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((r) => (
            <TableRow
              key={r.period}
              sx={{
                "&:hover": { backgroundColor: "#f0f7ff" },
                transition: "background-color 0.12s ease",
              }}
            >
              <TableCell
                align="center"
                sx={{
                  ...ROW_CELL_SX,
                  px: 2.5,
                  color: "#00336C",
                  fontWeight: 700,
                }}
              >
                {r.period}
              </TableCell>
              {visibleColumns.map((m) => {
                const val = r[m.key];
                const isNull = val == null;
                const isZero = val === 0;
                return (
                  <TableCell
                    key={m.key}
                    align="center"
                    sx={{
                      ...ROW_CELL_SX,
                      color: "#00336C",
                      fontWeight: isNull || isZero ? 400 : 600,
                    }}
                  >
                    {formatValue(val, m.format)}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}

          {/* Totals row */}
          <TableRow>
            <TableCell
              align="center"
              sx={{
                px: 2.5,
                py: 1.75,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                color: "#fff",
                backgroundColor: "#00336C",
                borderBottom: "none",
                whiteSpace: "nowrap",
              }}
            >
              Total
            </TableCell>
            {visibleColumns.map((m) => {
              const val = totals[m.key];
              return (
                <TableCell
                  key={m.key}
                  align="center"
                  sx={{
                    px: 2,
                    py: 1.75,
                    fontSize: 13,
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                    color: "#fff",
                    backgroundColor: "#00336C",
                    borderBottom: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatValue(val, m.format)}
                </TableCell>
              );
            })}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
